/* Partner Management: Hub rights (default + per-partner overrides) and team-leader follow-up notes (read-only). */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API, getAuthHeader } from "@/App";

const api = (m, p, d) => axios({ method: m, url: `${API}${p}`, data: d, headers: getAuthHeader() }).then((r) => r.data);
const err = (e) => toast.error(e?.response?.data?.detail || "Failed");

const Cell = ({ val, def, onSet, testid }) => {
  const eff = val === undefined ? def : val;
  const next = val === undefined ? !def : val === !def ? null : !val;
  return (
    <button onClick={() => onSet(next)} title={val === undefined ? "Following the default — click to override" : "Own setting — click to change"}
      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${eff ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"} ${val === undefined ? "opacity-60" : "ring-1 ring-slate-400"}`} data-testid={testid}>
      {eff ? "On" : "Off"}{val === undefined ? "" : " ✱"}</button>
  );
};

export const HubRightsTab = () => {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api("get", "/portal-admin/rights").then(setD).catch(err);
  useEffect(() => { load(); }, []);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-teal-600" />;
  const keys = Object.keys(d.labels);
  const setDefault = (k, v) => api("put", "/portal-admin/rights", { default: { [k]: v } }).then(() => { toast.success("Default saved"); load(); }).catch(err);
  const setOne = (pid, k, v) => api("put", "/portal-admin/rights", { partner_id: pid, key: k, value: v }).then(load).catch(err);
  const rows = d.partners.filter((p) => !q || `${p.name} ${p.codes.join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2" data-testid="hub-rights-tab">
      <p className="text-xs text-slate-500">Top row = default for every partner. Click a partner's cell to give them their own setting (marked ✱); click again until it fades to follow the default. Leaders are always view-only.</p>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search partner or code" className="h-8 w-56 rounded-md border px-2 text-sm" data-testid="hub-rights-search" />
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500"><tr><th className="px-3 py-2">Partner</th>{keys.map((k) => <th key={k} className="px-2 py-2">{d.labels[k]}</th>)}</tr></thead>
          <tbody>
            <tr className="border-t bg-teal-50/60"><td className="px-3 py-2 font-semibold">Default (everyone)</td>
              {keys.map((k) => <td key={k} className="px-2 py-2"><button onClick={() => setDefault(k, !d.default[k])} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${d.default[k] ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"}`} data-testid={`hub-right-default-${k}`}>{d.default[k] ? "On" : "Off"}</button></td>)}</tr>
            {rows.map((p) => (
              <tr key={p.id} className="border-t" data-testid={`hub-rights-row-${p.id}`}>
                <td className="px-3 py-2"><b>{p.name}</b> <span className="font-mono text-[11px] text-slate-500">{p.codes.join(", ")}</span></td>
                {keys.map((k) => <td key={k} className="px-2 py-2"><Cell val={(p.rights || {})[k]} def={d.default[k]} onSet={(v) => setOne(p.id, k, v)} testid={`hub-right-${p.id}-${k}`} /></td>)}
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const LeaderNotesAdminTab = () => {
  const [n, setN] = useState(null);
  const [code, setCode] = useState("");
  useEffect(() => { api("get", `/portal-admin/leader-notes${code ? `?code=${code}` : ""}`).then((r) => setN(r.notes)).catch(err); }, [code]);
  return (
    <div className="space-y-2" data-testid="leader-notes-admin">
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().trim())} placeholder="Filter by code (e.g. HB)" className="h-8 w-48 rounded-md border px-2 font-mono text-sm" data-testid="leader-notes-admin-code" />
      {!n ? <Loader2 className="h-5 w-5 animate-spin text-teal-600" /> : (
        <ul className="divide-y rounded-lg border bg-white text-sm">
          {n.map((x) => (
            <li key={x.id} className="flex gap-2 px-3 py-2" data-testid={`leader-notes-admin-${x.id}`}>
              <span className="rounded bg-slate-100 px-1.5 font-mono text-[11px]">{x.code}</span>
              <span className="flex-1"><span className={x.done ? "line-through opacity-60" : ""}>{x.text}</span>
                <span className="block text-[11px] text-slate-400">{x.leader_name} · {x.created_at.slice(0, 10)}{x.remind_on ? ` · remind ${x.remind_on}` : ""} · {x.share ? "shown to partner" : "private"}{x.done ? " · done" : ""}</span></span>
            </li>))}
          {!n.length && <li className="px-3 py-4 text-center text-xs text-slate-400">No leader notes yet.</li>}
        </ul>)}
    </div>
  );
};
